import moment from 'moment';
import {VNode} from 'snabbdom';
import {AttrsSchema, Component} from 'panel';

import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {template} from './template';
import css from './index.inline.styl';
import {
  CalendarDay,
  CalendarMonth,
  generateMonthGridLayout,
  generateSelectionLayout,
  generateVisibleCalendarRange,
  getCalendarDayKey,
  getCalendarMonthKey,
  getCalendarRangeKey,
  getDayLayoutFromDate,
  getScrollYFocusedOnDate,
  getScrollYFocusedOnDateSelection,
  MonthGridLayout,
  ScrollingCalendarDateSelection,
  SelectionLayout,
  SelectionSegmentLayout,
  SelectionSegmentType,
} from './util';

const HEADER_HEIGHT_PX = 26;
const ROW_HEIGHT_PX = 35;
const MONTH_PADDING = 8; // number of months to render above and below the "focal" month to provide scrolling runway
const HIGHLIGHT_SEGMENT_COLOR = colors.blue1;
const FILL_SEGMENT_COLOR = `rgba(${colors.blue1RGB}, 0.15)`;
const HIGHLIGHT_GRADIENT_START_COLOR = `rgba(${colors.blue1RGB}, 0)`;
const OFFSET_SEGMENT_COLOR = colors.gray1;
const OFFSET_GRADIENT_START_COLOR = `rgba(${colors.gray1RGB}, 0)`;

type CanaryVisibleCallback = (canaryKey: any, canaryEntry: IntersectionObserverEntry) => void;

class CanaryWatcher {
  private canaryLookup: Map<any, Element> = new Map<any, Element>();
  private keyLookup: Map<Element, any> = new Map<Element, any>();
  private observer: null | IntersectionObserver = null;
  private onCanaryVisible: CanaryVisibleCallback;

  constructor(onCanaryVisible: CanaryVisibleCallback) {
    this.onCanaryVisible = onCanaryVisible;
  }

  public watchViewport(viewport: Element): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new IntersectionObserver(this.handleObserverCallback.bind(this), {
      root: viewport,
      threshold: 0,
    });
    for (const canary of this.canaryLookup.values()) {
      this.observer.observe(canary);
    }
  }

  // the @canary parameter is a DOM element that, when scrolled into full view, suggests you should reload visible items to fill scrollable space
  public watchCanary(key: any, canary: Element): void {
    this.canaryLookup.set(key, canary);
    this.keyLookup.set(canary, key);
    if (this.observer) {
      this.observer.observe(canary);
    }
  }

  public disconnect(): void {
    this.observer?.disconnect();
    this.canaryLookup.clear();
    this.keyLookup.clear();
    this.observer = null;
  }

  private handleObserverCallback(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.onCanaryVisible(this.keyLookup.get(entry.target), entry);
      }
    }
  }
}

interface ScrollFocusTargetDate {
  date: moment.Moment;
  preferNearTop: boolean;
}

interface ScrollingCalendarAttributes {
  // ISO 8601 date string or datetime string. datetime string is used only with projects with Query Timezone.
  // TODO @jun consolidate `today` to be datetime string in query timezone
  today: string;
  'start-of-week': number;
  'disabled-until-date': string;
  'disabled-start-date': string;
  selection: null | ScrollingCalendarDateSelection;
  'preview-selection': null | ScrollingCalendarDateSelection;
}

export class ScrollingCalendar extends Component<EmptyObj, ScrollingCalendarAttributes> {
  private visibleCalendarRange?: [CalendarMonth, CalendarMonth];
  private monthGridCache: Map<string, MonthGridLayout> = new Map<string, MonthGridLayout>(); // key from getCalendarRangeKey()
  private scrollCanaryWatcher: CanaryWatcher = new CanaryWatcher(this.onScrollCanaryVisible.bind(this));
  private scrollContainer?: Element;
  private scrollContainerViewSize?: DOMRect;
  private didFirstScroll = false;

  // When this is set, the scroll container will imperatively scroll once during the next postpatch.
  // Scrolling during the postpatch hook guarantees the scroll is executed after a valid render cycle,
  // unlike requestAnimationFrame() which can sometimes trigger after the element is destroyed.
  private scheduledScrollY: null | number = null;

  // see deferUntilContainerVisible()
  private scrollContainerVisibilityObserver?: IntersectionObserver;

  // see deferUntilMouseup()
  private mouseupListener: null | EventListener = null;

  // We use CalendarDay as the argument type because it's the most raw & basic value possible, which is optimal for this callback that is executed for every rendered date cell.
  // If we passed in a more complex value as the argument, we would need to transform/construct a complex value many many times on every render.
  public dayTooltipRenderer: null | ((day: CalendarDay) => null | VNode) = null;

  static get attrsSchema(): AttrsSchema<ScrollingCalendarAttributes> {
    return {
      ...super.attrsSchema,
      today: {
        type: `string`,
        description: `ISO 8601 string representing today's date (optional, component will default to the system's current date)`,
      },
      'start-of-week': {type: `number`, default: 0, description: `Weekday integer where 0 = Sunday, 6 = Saturday`},
      'disabled-until-date': {
        type: `string`,
        description: `ISO 8601 string representing date from which all prior dates are disabled (excluding the specified date itself), scrolling may also be limited beyond this date`,
      },
      'disabled-start-date': {
        type: `string`,
        description: `ISO 8601 string representing date from which all subsequent dates are disabled (excluding the specified date itself), scrolling may also be limited beyond this date`,
      },
      selection: {
        type: `json`,
        default: null,
        description: `Object containing properties "start" & "end" (ISO 8601 date strings) and "highlightStart" & "highlightEnd" (boolean choice to visually highlight respective dates) and "offsetFrom" (ISO 8601 date string, date must be before "start" or after "end")`,
      },
      'preview-selection': {
        type: `json`,
        default: null,
        description: `Object (same format as for "selection" attribute) that represents an unsaved selection`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      helpers: {
        HEADER_HEIGHT_PX,
        ROW_HEIGHT_PX,
        SelectionSegmentType,
        getOrderedWeekdayLabels: (): string[] => {
          const weekdayStartOfWeek: number = this.attr(`start-of-week`);
          const labels = [];
          for (let dayOfWeek: number = weekdayStartOfWeek; dayOfWeek < weekdayStartOfWeek + 7; dayOfWeek++) {
            labels.push(
              moment()
                .weekday(dayOfWeek % 7)
                .format(`dd`),
            );
          }
          return labels;
        },
        getVisibleMonthGridLayout: (): MonthGridLayout => this.getMonthGridLayout(this.visibleCalendarRange),
        getTodayKey: (): string => {
          const today = this.getTodayDate();
          return getCalendarDayKey({year: today.year(), month: today.month(), isoDayOfMonth: today.date()});
        },
        getSelectionLayout: (): SelectionLayout => {
          const selection = this.attr(`selection`);
          if (selection) {
            return generateSelectionLayout(selection, this.helpers.getVisibleMonthGridLayout());
          }
          return null;
        },
        getPreviewSelectionLayout: (): SelectionLayout => {
          const previewSelection = this.attr(`preview-selection`);
          if (previewSelection) {
            return generateSelectionLayout(previewSelection, this.helpers.getVisibleMonthGridLayout());
          }
          return null;
        },
        getSelectionSegmentForDayKey: (
          dayKey: string,
          selectionLayout: null | SelectionLayout,
        ): null | SelectionSegmentLayout => {
          if (selectionLayout) {
            return selectionLayout.activeSegmentForDays.get(dayKey) || null;
          }
          return null;
        },
        getSelectionSegmentBackground: (segment: SelectionSegmentLayout): string => {
          let segmentColor: string;
          let segmentColorGradientStart: string;
          switch (segment.segmentType) {
            case SelectionSegmentType.Highlight:
              segmentColor = HIGHLIGHT_SEGMENT_COLOR;
              segmentColorGradientStart = HIGHLIGHT_GRADIENT_START_COLOR;
              break;
            case SelectionSegmentType.Fill:
              segmentColor = FILL_SEGMENT_COLOR;
              segmentColorGradientStart = HIGHLIGHT_GRADIENT_START_COLOR;
              break;
            case SelectionSegmentType.Offset:
              segmentColor = OFFSET_SEGMENT_COLOR;
              segmentColorGradientStart = OFFSET_GRADIENT_START_COLOR;
              break;

            default: {
              const unreachable: never = segment.segmentType; // a trick to enforce full enum coverage in switch case
              throw new Error(`unknown selection segment type ${unreachable}`);
            }
          }

          if (segment.fadeOut || segment.fadeIn) {
            const columnPercent = Math.round(100 / segment.columnSpan);
            const colorStopList = [];
            if (segment.fadeIn) {
              colorStopList.push(`${segmentColorGradientStart} 0%`);
              colorStopList.push(`${segmentColor} ${columnPercent}%`);
            }
            if (segment.fadeOut) {
              colorStopList.push(`${segmentColor} ${columnPercent * (segment.columnSpan - 1)}%`);
              colorStopList.push(`${segmentColorGradientStart} 100%`);
            }
            return `linear-gradient(to right, ${colorStopList.join(`, `)})`;
          }
          return segmentColor;
        },
        renderDayTooltip: (day: CalendarDay): null | VNode => this.dayTooltipRenderer?.(day),

        handleScrollContainerPostpatch: (vnode: VNode) => {
          const el = vnode.elm as HTMLElement;
          if (this.scheduledScrollY !== null) {
            el.scrollTo({top: this.scheduledScrollY, left: 0, behavior: `auto`});
            this.scheduledScrollY = null; // clear after use, we only want to scroll-jump once
          }
        },
        handleTopCanaryInsert: (vnode: VNode) => {
          const el = vnode.elm as HTMLInputElement;
          this.scrollCanaryWatcher.watchCanary(`top`, el);
        },
        handleBottomCanaryInsert: (vnode: VNode) => {
          const el = vnode.elm as HTMLInputElement;
          this.scrollCanaryWatcher.watchCanary(`bottom`, el);
        },
        handleScrollContainerMousedown: (): void => {
          // We track all mousedown events so that we can block scroll-jumping while the user is actively clicking the mouse (see onScrollCanaryVisible),
          // because of the possibility that the user could be actively clicking and dragging the scrollbar which could conflict with any programmatic scroll-jumping we do.
          // We do this on all mousedown events as a simple workaround because there is no simple, convenient way to interrupt scrolling or target scrollbar clicks specifically.
          this.deferUntilMouseup(); // deferUntilMouseup() cleans up mouse listeners on mouseup
        },
        handleDelegatedClickHandler: (ev: MouseEvent) => {
          const calendarDay = ScrollingCalendar.getCalendarDayFromDelegatedMouseEvent(ev, false);
          if (calendarDay) {
            const isoString = moment({
              year: calendarDay.year,
              month: calendarDay.month,
              day: calendarDay.isoDayOfMonth,
            }).toISOString();
            this.dispatchEvent(new CustomEvent(`dateclick`, {detail: {date: isoString}}));
          }
        },
        handleDelegatedMouseEnterHandler: (ev: MouseEvent): void => {
          const calendarDay = ScrollingCalendar.getCalendarDayFromDelegatedMouseEvent(ev, true);
          if (calendarDay) {
            const isoString = moment({
              year: calendarDay.year,
              month: calendarDay.month,
              day: calendarDay.isoDayOfMonth,
            }).toISOString();
            this.dispatchEvent(new CustomEvent(`datemouseenter`, {detail: {date: isoString}}));
          }
        },
        handleDelegatedMouseLeaveHandler: (ev: MouseEvent): void => {
          const calendarDay = ScrollingCalendar.getCalendarDayFromDelegatedMouseEvent(ev, true);
          if (calendarDay) {
            const isoString = moment({
              year: calendarDay.year,
              month: calendarDay.month,
              day: calendarDay.isoDayOfMonth,
            }).toISOString();
            this.dispatchEvent(new CustomEvent(`datemouseleave`, {detail: {date: isoString}}));
          }
        },
      },
    };
  }

  static getCalendarDayFromDelegatedMouseEvent(ev: MouseEvent, containerOnly: boolean): null | CalendarDay {
    const target: Element = ev.target as Element;
    if (target.classList) {
      // make sure it's an element
      const containerElement: Element =
        !containerOnly && target.classList.contains(`day-of-month`) ? target.parentElement : target;
      if (
        containerElement.classList.contains(`day-of-month-square-container`) &&
        !containerElement.classList.contains(`disabled`)
      ) {
        return (containerElement as any).calendarDay as CalendarDay;
      }
    }
    return null;
  }

  attributeChangedCallback(attributeName: string, oldValue: any, newValue: any) {
    switch (attributeName) {
      case `start-of-week`:
      case `disabled-start-date`: {
        // these attributes are baked into the month grid layout, cached layouts must be cleared when they change
        this.monthGridCache.clear();
        break;
      }
    }

    super.attributeChangedCallback(attributeName, oldValue, newValue);
  }

  connectedCallback() {
    // generate fairly complete layout info before first render to avoid flashes of unstructured content
    const focalTarget = this.getFocalTarget();
    this.visibleCalendarRange = this.generateVisibleCalendarRangeFocusedOnDate(focalTarget.date);

    super.connectedCallback(); // first render

    // snabbdom insert hooks don't fire on the root element, so we initialize the scroll container here instead
    this.scrollContainer = this.el.querySelector(`.scroll-container`);
    this.scrollContainerViewSize = this.scrollContainer.getBoundingClientRect();

    // check if view is initially invisible
    // note: scrollTo() typically deferred until next frame's postpatch, but the first postpatch fires when scroll container is not connected to the DOM
    if (this.scrollContainerViewSize.width === 0 || this.scrollContainerViewSize.height === 0) {
      this.deferUntilContainerVisible(() => {
        // recalculate size
        this.scrollContainerViewSize = this.scrollContainer.getBoundingClientRect();
        this.scrollContainer.scrollTo({top: this.getDefaultScrollY(), left: 0, behavior: `auto`});
        this.scrollCanaryWatcher.watchViewport(this.scrollContainer);
        this.didFirstScroll = true;
      });
    } else {
      this.scrollContainer.scrollTo({top: this.getDefaultScrollY(), left: 0, behavior: `auto`});
      this.scrollCanaryWatcher.watchViewport(this.scrollContainer);
      this.didFirstScroll = true;
    }
  }

  disconnectedCallback() {
    this.scrollCanaryWatcher.disconnect();
    super.disconnectedCallback();
  }

  // only apply scroll limits on browsers that support overscroll chain prevention (ie. everyone but Safari and some niche mobile browsers),
  // otherwise it's too easy to hit scroll limits and trigger scroll chaining which can be annoying
  private shouldLimitScroll(): boolean {
    return CSS.supports(`overscroll-behavior`, `contain`);
  }

  private getTodayDate(): moment.Moment {
    const isoTodayString = this.attr(`today`);
    if (isoTodayString) {
      const today = moment(isoTodayString, moment.ISO_8601);
      if (!today.isValid()) {
        throw new Error(`"today" attribute must be a valid ISO 8601 date string`);
      }
      return today;
    }
    return moment();
  }

  private getDisabledStartDate(): null | moment.Moment {
    const isoString = this.attr(`disabled-start-date`);
    if (isoString) {
      const disabledStartDate = moment(isoString, moment.ISO_8601);
      if (!disabledStartDate.isValid()) {
        throw new Error(`"disabled-start-date" attribute must be a valid ISO 8601 date string`);
      }
      return disabledStartDate;
    }
    return null;
  }

  private getDisabledUntilDate(): null | moment.Moment {
    const isoString = this.attr(`disabled-until-date`);
    if (isoString) {
      const disabledUntilDate = moment(isoString, moment.ISO_8601);
      if (!disabledUntilDate.isValid()) {
        throw new Error(`"disabled-until-date" attribute must be a valid ISO 8601 date string`);
      }
      return disabledUntilDate;
    }
    return null;
  }

  private getMonthGridLayout(calendarRange: [CalendarMonth, CalendarMonth]): MonthGridLayout {
    const rangeKey = getCalendarRangeKey(calendarRange);
    if (!this.monthGridCache.has(rangeKey)) {
      this.monthGridCache.set(
        rangeKey,
        generateMonthGridLayout(
          calendarRange,
          this.attr(`start-of-week`),
          this.getDisabledStartDate(),
          this.getDisabledUntilDate(),
        ),
      );
    }
    return this.monthGridCache.get(rangeKey);
  }

  private getFocalTarget(): ScrollFocusTargetDate {
    let preferNearTop = false;
    let notableDate: moment.Moment = this.getTodayDate();

    const selection = this.attr(`selection`);
    if (selection) {
      preferNearTop = selection.highlightStart;
      notableDate = moment(preferNearTop ? selection.start : selection.end, moment.ISO_8601);
    }

    // clamp to disabled dates
    const disabledStartDate = this.getDisabledStartDate();
    const disabledUntilDate = this.getDisabledUntilDate();
    notableDate = disabledStartDate?.isBefore(notableDate) ? disabledStartDate : notableDate;
    notableDate = disabledUntilDate?.isAfter(notableDate) ? disabledUntilDate : notableDate;
    return {date: notableDate, preferNearTop};
  }

  private getDefaultScrollY(): number {
    const focalTarget = this.getFocalTarget();
    const selection = this.attr(`selection`);
    if (selection) {
      return getScrollYFocusedOnDateSelection(
        selection,
        this.helpers.getVisibleMonthGridLayout(),
        this.scrollContainerViewSize.height,
        ROW_HEIGHT_PX,
        HEADER_HEIGHT_PX,
        this.getDisabledStartDate(),
        this.getDisabledUntilDate(),
      );
    } else {
      return getScrollYFocusedOnDate(
        focalTarget.date,
        this.helpers.getVisibleMonthGridLayout(),
        this.scrollContainerViewSize.height,
        ROW_HEIGHT_PX,
        HEADER_HEIGHT_PX,
        focalTarget.preferNearTop,
        this.getDisabledStartDate(),
        this.getDisabledUntilDate(),
      );
    }
  }

  private deferUntilContainerVisible(deferredAction?: () => void): void {
    if (this.scrollContainerVisibilityObserver) {
      this.scrollContainerVisibilityObserver.disconnect();
      delete this.scrollContainerVisibilityObserver;
    }

    this.scrollContainerVisibilityObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.scrollContainerVisibilityObserver.disconnect();
            delete this.scrollContainerVisibilityObserver;
            deferredAction?.call(this);
          }
        }
      },
      {root: null, threshold: 0},
    );

    this.scrollContainerVisibilityObserver.observe(this.scrollContainer);
  }

  private deferUntilMouseup(deferredAction?: () => void): void {
    if (this.mouseupListener) {
      window.removeEventListener(`mouseup`, this.mouseupListener);
    }

    this.mouseupListener = () => {
      window.removeEventListener(`mouseup`, this.mouseupListener);
      this.mouseupListener = null;
      deferredAction?.call(this);
    };
    window.addEventListener(`mouseup`, this.mouseupListener);
  }

  private generateVisibleCalendarRangeFocusedOnDate(focalDate: moment.Moment): [CalendarMonth, CalendarMonth] {
    return generateVisibleCalendarRange(
      {year: focalDate.year(), month: focalDate.month()},
      MONTH_PADDING,
      this.shouldLimitScroll() ? this.getDisabledStartDate() : null,
      this.shouldLimitScroll() ? this.getDisabledUntilDate() : null,
    );
  }

  // when our canaries scroll into view and become visible, we heed its figurative warning to load more content around where the user has scrolled to
  private onScrollCanaryVisible(canaryKey: string, _): void {
    const isTop = canaryKey === `top`;

    const currentLayout = this.helpers.getVisibleMonthGridLayout();
    const newFocalMonth = isTop ? currentLayout.topCanaryCalendarMonth : currentLayout.bottomCanaryCalendarMonth;
    const oldRowStartForFocal = isTop ? currentLayout.topCanaryRowStart : currentLayout.bottomCanaryRowStart;

    const newCalendarRange = generateVisibleCalendarRange(
      newFocalMonth,
      MONTH_PADDING,
      this.shouldLimitScroll() ? this.getDisabledStartDate() : null,
      this.shouldLimitScroll() ? this.getDisabledUntilDate() : null,
    );
    const newLayout = this.getMonthGridLayout(newCalendarRange);

    let newRowStartForFocal: number = null;
    if (isTop) {
      newRowStartForFocal = newLayout.months.get(getCalendarMonthKey(newFocalMonth)).rowStart;
    } else {
      newRowStartForFocal = newLayout.days.get(
        getCalendarDayKey({...newFocalMonth, isoDayOfMonth: moment(newFocalMonth).daysInMonth()}),
      ).rowStart;
    }

    const layoutYDiff = (newRowStartForFocal - oldRowStartForFocal) * ROW_HEIGHT_PX;

    const isMousedown = this.mouseupListener !== null;
    if (isMousedown) {
      // it's possible that the user is clicking and dragging the scrollbar,
      // which could cause a conflict between programmatic scrollTo() and the user's scrollbar
      this.deferUntilMouseup(() => {
        this.visibleCalendarRange = newCalendarRange;
        this.scheduledScrollY = this.scrollContainer.scrollTop + layoutYDiff;
        this.update();
      });
    } else {
      this.visibleCalendarRange = newCalendarRange;
      this.scheduledScrollY = this.scrollContainer.scrollTop + layoutYDiff;
      this.update();
    }
  }

  public scrollDateSelectionIntoView(
    selection: ScrollingCalendarDateSelection,
    preferShowStart: boolean, // preference to show start date (if true) or end date (if false) when both dates cannot be shown at once
  ): void {
    if (this.didFirstScroll) {
      const monthGridLayout = this.helpers.getVisibleMonthGridLayout();
      const startDay = getDayLayoutFromDate(moment(selection.start, moment.ISO_8601), monthGridLayout);
      const endDay = getDayLayoutFromDate(moment(selection.end, moment.ISO_8601), monthGridLayout);

      const isSelectionOutsideGrid = preferShowStart ? !startDay : !endDay;
      if (isSelectionOutsideGrid) {
        const focalDate = moment(preferShowStart ? selection.start : selection.end, moment.ISO_8601);
        this.visibleCalendarRange = this.generateVisibleCalendarRangeFocusedOnDate(focalDate);
      }

      this.scheduledScrollY = getScrollYFocusedOnDateSelection(
        selection,
        this.helpers.getVisibleMonthGridLayout(),
        this.scrollContainerViewSize.height,
        ROW_HEIGHT_PX,
        HEADER_HEIGHT_PX,
        this.getDisabledStartDate(),
        this.getDisabledUntilDate(),
        preferShowStart /* preferFocusStart */,
      );
      this.update();
    }
  }

  public scrollDateIntoView(
    focalDate: moment.Moment,
    preferNearTop: boolean, // preference for the focalDate to be positioned near the top of the viewport (or near the bottom if false)
  ) {
    if (this.didFirstScroll) {
      const monthGridLayout = this.helpers.getVisibleMonthGridLayout();
      const focalDay = getDayLayoutFromDate(focalDate, monthGridLayout);

      if (!focalDay) {
        this.visibleCalendarRange = this.generateVisibleCalendarRangeFocusedOnDate(focalDate);
      }

      this.scheduledScrollY = getScrollYFocusedOnDate(
        focalDate,
        this.helpers.getVisibleMonthGridLayout(),
        this.scrollContainerViewSize.height,
        ROW_HEIGHT_PX,
        HEADER_HEIGHT_PX,
        preferNearTop,
        this.getDisabledStartDate(),
        this.getDisabledUntilDate(),
      );
      this.update();
    }
  }
}

export const ScrollingCalendarEl = defineAndMakeJsxFactory(`mp-scrolling-calendar`, ScrollingCalendar);
export const ScrollingCalendarReact = panel2react(`mp-scrolling-calendar`, ScrollingCalendar);
